import React, { useState, useEffect } from "react";
import { ChevronLeft, ChevronRight, Sparkles } from "lucide-react";
import { Link } from "react-router-dom";

const LuxuryAnimatedHero = () => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isAnimating, setIsAnimating] = useState(false);

  const luxurySlides = [
    {
      image: "https://christiansonlee.com/wp-content/uploads/2016/02/venetian-plaster-grand-entry-foyer-walls-gilded-dome-painted-floor-nj_1.jpg",
      title: "Grand Estate Collection",
      subtitle: "Ultra-Wide Engineered Hardwood",
      description: "Majestic wide-format flooring crafted for distinguished homes and luxury residences",
      cta: "Explore Collection",
      accent: "PREMIUM WIDE FORMAT"
    },
    {
      image: "https://images.pexels.com/photos/129731/pexels-photo-129731.jpeg?w=1920&q=90",
      title: "Ultra-Luxury Laminate",
      subtitle: "Innovative Technology",
      description: "Discover waterproof laminate flooring with photorealistic wood grain and commercial-grade durability",
      cta: "View Products",
      accent: "PREMIUM CRAFTSMANSHIP"
    },
    {
      image: "https://images.unsplash.com/photo-1519393890420-f28727375fa5?w=1920&q=90",
      title: "Architectural Masterpieces",
      subtitle: "Wide Format Luxury",
      description: "Transform your space with wide-format engineered hardwood in sophisticated chevron layouts",
      cta: "Discover More",
      accent: "DESIGNER CHOICE"
    },
    {
      image: "https://images.pexels.com/photos/172289/pexels-photo-172289.jpeg?w=1920&q=90",
      title: "Engineered Hardwood",
      subtitle: "Waterproof Excellence",
      description: "Premium engineered flooring combining style, stability, and superior water resistance",
      cta: "Learn More",
      accent: "TECHNOLOGY MEETS BEAUTY"
    },
    {
      image: "https://images.pexels.com/photos/1571460/pexels-photo-1571460.jpeg?w=1920&q=90",
      title: "Artisan Wood Grains",
      subtitle: "Natural Sophistication",
      description: "Celebrate authentic wood character with hand-scraped textures and organic color variations",
      cta: "Shop Now",
      accent: "TIMELESS ELEGANCE"
    }
  ];

  useEffect(() => {
    const interval = setInterval(() => {
      nextSlide();
    }, 6000); // Auto-advance every 6 seconds

    return () => clearInterval(interval);
  }, [currentSlide]);

  const nextSlide = () => {
    if (!isAnimating) {
      setIsAnimating(true);
      setCurrentSlide((prev) => (prev + 1) % luxurySlides.length);
      setTimeout(() => setIsAnimating(false), 1000);
    }
  };

  const prevSlide = () => {
    if (!isAnimating) {
      setIsAnimating(true);
      setCurrentSlide((prev) => (prev - 1 + luxurySlides.length) % luxurySlides.length);
      setTimeout(() => setIsAnimating(false), 1000);
    }
  };

  const goToSlide = (index) => {
    if (!isAnimating && index !== currentSlide) {
      setIsAnimating(true);
      setCurrentSlide(index);
      setTimeout(() => setIsAnimating(false), 1000);
    }
  };

  return (
    <section className="relative h-screen overflow-hidden">
      {/* Background Slides */}
      <div className="absolute inset-0">
        {luxurySlides.map((slide, index) => (
          <div
            key={index}
            className={`absolute inset-0 transition-all duration-1000 ease-in-out ${
              index === currentSlide
                ? "opacity-100 scale-100"
                : "opacity-0 scale-105"
            }`}
          >
            {/* Image */}
            <div className="absolute inset-0">
              <img
                src={slide.image}
                alt={slide.title}
                className="w-full h-full object-cover"
              />
            </div>

            {/* Gradient Overlays */}
            <div className="absolute inset-0 bg-gradient-to-r from-luxury-navy/90 via-luxury-navy/60 to-transparent"></div>
            <div className="absolute inset-0 bg-gradient-to-t from-luxury-navy/80 via-transparent to-transparent"></div>
            
            {/* Luxury Pattern Overlay */}
            <div className="absolute inset-0 opacity-10">
              <div className="absolute inset-0" style={{
                backgroundImage: 'radial-gradient(circle at 2px 2px, rgba(212, 175, 55, 0.6) 1px, transparent 0)',
                backgroundSize: '60px 60px'
              }}></div>
            </div>
          </div>
        ))}
      </div>

      {/* Content */}
      <div className="relative h-full flex items-center z-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full">
          <div className="max-w-3xl">
            {luxurySlides.map((slide, index) => (
              <div
                key={index}
                className={`transition-all duration-1000 ${
                  index === currentSlide
                    ? "opacity-100 transform translate-x-0"
                    : "opacity-0 transform -translate-x-12 absolute"
                }`}
              >
                {/* Accent Label */}
                <div className="inline-flex items-center mb-3 sm:mb-6 bg-luxury-gold/20 backdrop-blur-sm border border-luxury-gold/40 px-3 sm:px-6 py-1 sm:py-2 rounded-sm">
                  <Sparkles className="w-3 h-3 sm:w-4 sm:h-4 text-luxury-gold mr-1 sm:mr-2" />
                  <span className="text-luxury-gold text-xs sm:text-sm font-semibold tracking-wider uppercase">
                    {slide.accent}
                  </span>
                </div>

                {/* Main Title */}
                <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl xl:text-7xl font-bold text-white mb-2 sm:mb-4 luxury-heading leading-tight">
                  {slide.title}
                </h1>

                {/* Subtitle */}
                <h2 className="text-lg sm:text-xl md:text-2xl lg:text-3xl text-luxury-gold mb-3 sm:mb-6 luxury-heading italic">
                  {slide.subtitle}
                </h2>

                {/* Gold Divider */}
                <div className="w-16 sm:w-24 md:w-32 h-1 bg-luxury-gold mb-4 sm:mb-8 luxury-shimmer"></div>

                {/* Description */}
                <p className="text-sm sm:text-base md:text-lg lg:text-xl text-gray-200 mb-6 sm:mb-10 leading-relaxed max-w-2xl">
                  {slide.description}
                </p>

                {/* CTA Buttons */}
                <div className="flex flex-col sm:flex-row gap-3 sm:gap-4">
                  <Link
                    to="/products"
                    className="inline-flex items-center justify-center luxury-button text-luxury-navy px-6 sm:px-8 md:px-10 py-3 sm:py-4 md:py-5 rounded-sm font-bold text-sm sm:text-base md:text-lg group"
                  >
                    <span>{slide.cta}</span>
                    <svg className="w-4 h-4 sm:w-5 sm:h-5 ml-2 group-hover:translate-x-2 transition-transform duration-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
                    </svg>
                  </Link>
                  <Link
                    to="/request-quote"
                    className="inline-flex items-center justify-center bg-white/10 backdrop-blur-sm border-2 border-white text-white px-6 sm:px-8 md:px-10 py-3 sm:py-4 md:py-5 rounded-sm font-bold text-sm sm:text-base md:text-lg hover:bg-white hover:text-luxury-navy transition-all duration-300"
                  >
                    Get Quote
                  </Link>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Navigation Arrows - Hidden on mobile, visible on tablet+ */}
      <button
        onClick={prevSlide}
        className="hidden sm:block absolute left-2 sm:left-4 md:left-8 top-1/2 -translate-y-1/2 z-20 bg-luxury-gold/20 backdrop-blur-sm border border-luxury-gold/40 text-luxury-gold p-2 sm:p-3 md:p-4 rounded-sm hover:bg-luxury-gold hover:text-luxury-navy transition-all duration-300 group"
        aria-label="Previous slide"
      >
        <ChevronLeft className="w-5 h-5 sm:w-6 sm:h-6 md:w-8 md:h-8 group-hover:-translate-x-1 transition-transform duration-300" />
      </button>
      <button
        onClick={nextSlide}
        className="hidden sm:block absolute right-2 sm:right-4 md:right-8 top-1/2 -translate-y-1/2 z-20 bg-luxury-gold/20 backdrop-blur-sm border border-luxury-gold/40 text-luxury-gold p-2 sm:p-3 md:p-4 rounded-sm hover:bg-luxury-gold hover:text-luxury-navy transition-all duration-300 group"
        aria-label="Next slide"
      >
        <ChevronRight className="w-5 h-5 sm:w-6 sm:h-6 md:w-8 md:h-8 group-hover:translate-x-1 transition-transform duration-300" />
      </button>

      {/* Slide Indicators */}
      <div className="absolute bottom-6 sm:bottom-12 left-1/2 -translate-x-1/2 z-20 flex gap-2 sm:gap-3">
        {luxurySlides.map((_, index) => (
          <button
            key={index}
            onClick={() => goToSlide(index)}
            className={`transition-all duration-500 rounded-sm ${
              index === currentSlide
                ? "w-8 sm:w-12 md:w-16 h-1.5 sm:h-2 bg-luxury-gold"
                : "w-4 sm:w-6 md:w-8 h-1.5 sm:h-2 bg-white/40 hover:bg-white/60"
            }`}
            aria-label={`Go to slide ${index + 1}`}
          />
        ))}
      </div>

      {/* Factory Direct Badge - Smaller on mobile */}
      <div className="absolute top-2 sm:top-4 md:top-8 right-2 sm:right-4 md:right-8 z-20 bg-luxury-gold/90 backdrop-blur-sm px-3 sm:px-5 md:px-8 py-2 sm:py-3 md:py-4 rounded-sm border-2 border-luxury-gold">
        <p className="text-luxury-navy font-bold text-xs sm:text-sm md:text-lg luxury-heading">
          FACTORY DIRECT
        </p>
        <p className="text-luxury-navy text-xs sm:text-sm hidden sm:block">Premium Pricing</p>
      </div>

      {/* Scroll Indicator - Hidden on mobile */}
      <div className="hidden sm:block absolute bottom-20 sm:bottom-24 md:bottom-32 left-1/2 -translate-x-1/2 z-20 animate-bounce">
        <div className="flex flex-col items-center">
          <span className="text-white text-xs sm:text-sm mb-2 uppercase tracking-wider">Scroll</span>
          <svg className="w-5 h-5 sm:w-6 sm:h-6 text-luxury-gold" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 14l-7 7m0 0l-7-7m7 7V3" />
          </svg>
        </div>
      </div>
    </section>
  );
};

export default LuxuryAnimatedHero;
